import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, catchError, finalize, map, Observable, of, tap, throwError } from 'rxjs';
import { IFileResponseDTO, IUploadFileResponseDTO, ITemporaryFileUpload } from 'src/app/core/domain/file';
import { LoadingService } from 'src/app/core/services/loading.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { ToastService } from 'src/app/shared/utils/toast.service';
import { ErrorHandlerService } from 'src/app/core/services/error-handler.service';
import { ApiRoutingPolicyService } from 'src/app/core/services/api-routing-policy.service';

@Injectable({
  providedIn: 'root'
})
export class FilesService {
  // Base URL for the API
  private baseUrl = environment.apiUrl;
  private http = inject(HttpClient);
  private loadingService = inject(LoadingService);
  private translate = inject(TranslateService);
  private toastService = inject(ToastService);
  private errorHandlerService = inject(ErrorHandlerService);
  private apiRoutingPolicyService = inject(ApiRoutingPolicyService);

  constructor() { }

  getFiles(rettxid: string): Observable<IFileResponseDTO[]> {
    this.loadingService.show();
    const url = this.apiRoutingPolicyService.buildUrl('files', '/' + rettxid + '/files');
    return this.http.get<IFileResponseDTO[]>(url).pipe(
      map(response =>{
        this.toastService.showToast(this.translate.instant('CONTROL_MESSAGES.SUCCESS.GET_FILE'), 'success');
        return response;
      }),
      catchError(this.errorHandlerService.handleError('UserService::getFiles', 'CONTROL_MESSAGES.ERROR.GET_FILE', 'files', this.apiRoutingPolicyService.getTelemetryContext('files'))),
      finalize(() => this.loadingService.hide())
    );
  }

  // Get a specific file by its ID
  getFile(rettxid: string, fileId: string): Observable<IFileResponseDTO> {
    this.loadingService.show();
    const url = this.apiRoutingPolicyService.buildUrl('files', '/' + rettxid + '/files/' + fileId);
    return this.http.get<IFileResponseDTO>(url).pipe(
      finalize(() => this.loadingService.hide())
    );
  }

  downloadFile(fileUrl: string): Observable<Blob> {
    this.loadingService.show();

    const headers = new HttpHeaders({
       responseType: 'blob'
    });
    return this.http.get<Blob>(fileUrl, { headers }).pipe(
      finalize(() => this.loadingService.hide())
    );
  }

  // Get the upload file token from API
  getFileUploadToken(rettxid: string, fileName: string, fileType: 'genetic-report' | 'doctor-report' | 'generic' | 'unknown'): Observable<IUploadFileResponseDTO> {
      const params = new HttpParams()
        .set('file_name', fileName)
        .set('file_type', fileType);
  
      const url = this.apiRoutingPolicyService.buildUrl('files', '/' + rettxid + '/files/upload-file-info');
      return this.http.post<IUploadFileResponseDTO>(url, null, { params }).pipe(
        map(response => {
          return response;
        }),
        catchError(this.errorHandlerService.handleError('UserService::getFileUploadToken', 'CONTROL_MESSAGES.ERROR.UPLOAD_FILE', 'files', this.apiRoutingPolicyService.getTelemetryContext('files')))
      );
  }

  // Upload the file to Azure Blob Storage
  uploadFile(fileUrl: string, fileBlob: Blob): Observable<any> {
    const headers = new HttpHeaders({
      'x-ms-blob-type': 'BlockBlob',
      'x-ms-blob-content-type': fileBlob.type
    });

    return this.http.put(fileUrl, fileBlob, { headers }).pipe(
      map(response =>{
        return response;
      }),
      catchError(this.errorHandlerService.handleError('UserService::uploadFile', 'CONTROL_MESSAGES.ERROR.GET_FILE', 'files', this.apiRoutingPolicyService.getTelemetryContext('files'))),
    );
  }
}
